import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import {
  bulkResultMessage,
  type BulkActionRequest,
  type MoveApplicationRequest,
  type PipelineActionView,
  type PipelineRow,
} from '@nest-recruit/shared';
import { errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { moveApplication, pipelineKey, refreshOptions, startBulkAction, undoAction } from '../api/pipeline-api';
import { patched, type PipelineData, type RowPatch } from '../row-patch';
import { useStageLabels } from '../../../lib/modules';

export type { RowPatch } from '../row-patch';

/**
 * Every pipeline action, run the §5a way: the rows change the moment the recruiter
 * acts (rule 1), the change is held until the refetch has landed so nothing jumps back
 * (rule 3), and a failure puts the rows back and says why. Only the rows being changed
 * are marked while that happens (rule 4). A reversible action offers Undo (rule 8) —
 * for ten seconds in its toast, and for §L9's ten minutes in the action's report.
 */
export function usePipelineActions(jobId: string) {
  const stageLabels = useStageLabels();
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const [reportId, setReportId] = useState<string | null>(null);

  const mark = useCallback((ids: readonly string[], isOn: boolean) => {
    setPending((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (isOn) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  /** Draws the patch on every cached list page and board of this job; returns the way back. */
  const draw = useCallback(
    (ids: readonly string[], patch: RowPatch): (() => void) => {
      const snapshot = queryClient.getQueriesData<PipelineData>({ queryKey: pipelineKey(jobId) });
      const wanted = new Set(ids);
      queryClient.setQueriesData<PipelineData>({ queryKey: pipelineKey(jobId) }, (data) => (data ? patched(data, wanted, patch) : data));
      return () => {
        for (const [key, data] of snapshot) queryClient.setQueryData<PipelineData | undefined>(key as QueryKey, data);
      };
    },
    [queryClient, jobId],
  );

  /** The server's copy has landed — only now does the optimistic state let go. */
  const settle = useCallback(
    async (ids: readonly string[]): Promise<void> => {
      await queryClient.invalidateQueries({ queryKey: pipelineKey(jobId) });
      mark(ids, false);
    },
    [queryClient, jobId, mark],
  );

  const undo = useCallback(
    async (actionId: string): Promise<void> => {
      try {
        const view = await undoAction(actionId);
        await queryClient.invalidateQueries({ queryKey: pipelineKey(jobId) });
        const refused = view.undoRefusals.length;
        show({
          tone: refused === 0 ? 'info' : 'warning',
          message: refused === 0 ? `Undone — ${view.undoneCount} put back` : `${view.undoneCount} put back. ${refused} could not be — see details`,
        });
        if (refused > 0) setReportId(actionId);
      } catch (error) {
        show({ tone: 'danger', message: "Couldn't undo that", detail: errorMessage(error) });
      }
    },
    [queryClient, jobId, show],
  );

  /** F8.13 from a row or a card: one application, one move. */
  const moveOne = useCallback(
    async (row: PipelineRow, body: Omit<MoveApplicationRequest, 'version'>, via: 'board' | 'list'): Promise<boolean> => {
      const restore = draw([row.id], { stage: body.to });
      mark([row.id], true);
      try {
        const result = await moveApplication(row.id, { ...body, version: row.version }, via);
        await settle([row.id]);
        if ((body.newPoolNames ?? []).length > 0) void refreshOptions(queryClient);
        showUndo(`${row.candidate.fullName} moved to ${stageLabels[body.to]}`, () => void undo(result.action.id));
        return true;
      } catch (error) {
        restore();
        mark([row.id], false);
        show({ tone: 'danger', message: `${row.candidate.fullName} stayed where they were`, detail: errorMessage(error) });
        return false;
      }
    },
    [draw, mark, settle, undo, show, showUndo, queryClient, stageLabels],
  );

  /** §L26: "{n} of {total} updated. {m} failed — see details." Never "successfully". */
  const announce = useCallback(
    (view: PipelineActionView): void => {
      if (view.failedCount > 0) {
        setReportId(view.id);
        show({ tone: 'warning', message: bulkResultMessage(view) });
        return;
      }
      if (view.canUndo) {
        showUndo(bulkResultMessage(view), () => void undo(view.id));
        return;
      }
      show({ tone: 'success', message: bulkResultMessage(view) });
    },
    [show, showUndo, undo],
  );

  /** F8.11 and §L9's other bulk actions. Above 25 the report follows its progress. */
  const runBulk = useCallback(
    async (body: BulkActionRequest, visible: readonly PipelineRow[], patchFor: (row: PipelineRow) => RowPatch | null): Promise<void> => {
      const restores = visible.flatMap((row) => {
        const patch = patchFor(row);
        return patch ? [draw([row.id], patch)] : [];
      });
      const ids = visible.map((row) => row.id);
      mark(ids, true);
      try {
        const view = await startBulkAction(jobId, body);
        if (view.isAsync) {
          // Kept on screen while it runs; the report shows where it has got to.
          setReportId(view.id);
          await settle(ids);
          return;
        }
        await settle(ids);
        void refreshOptions(queryClient);
        announce(view);
      } catch (error) {
        for (const restore of restores) restore();
        mark(ids, false);
        show({ tone: 'danger', message: 'Nothing was changed', detail: errorMessage(error) });
      }
    },
    [draw, mark, settle, jobId, queryClient, show, announce],
  );

  return { pending, reportId, setReportId, moveOne, runBulk, undo, announce };
}
